import { useState, useEffect } from "react";
import { AuthProvider, useAuth } from "./lib/auth.jsx";
import ParentApp from "./parent/ParentApp.jsx";
import { ScreenPasswordReset } from "./parent/screens.jsx";
import PayReturn from "./parent/PayReturn.jsx";
import AdminDashboard from "./admin/AdminDashboard.jsx";
import AdminWorkspace from "./admin/AdminWorkspace.jsx";
import AdminRefunds from "./admin/AdminRefunds.jsx";
import AdminFreeGrants from "./admin/AdminFreeGrants.jsx";
import AdminArchive from "./admin/AdminArchive.jsx";
import AdminArchiveDetail from "./admin/AdminArchiveDetail.jsx";
import AdminParents from "./admin/AdminParents.jsx";
import AdminParentDetail from "./admin/AdminParentDetail.jsx";
import AdminStats from "./admin/AdminStats.jsx";

function ShellLoading() {
  return (
    <div
      className="fullscreen-shell"
      style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <div className="spinner" />
    </div>
  );
}

function AccessGate({ title, body, cta, href }) {
  return (
    <div
      style={{
        height: "100dvh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 14,
        padding: 28,
        textAlign: "center",
        background: "#f0eee9",
        fontFamily: "var(--font-sans)",
      }}
    >
      <div style={{ fontSize: 48 }}>🔒</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#1a1a2e" }}>{title}</div>
      {body && (
        <div style={{ fontSize: 13, color: "#4a5568", lineHeight: 1.7, maxWidth: 320 }}>
          {body}
        </div>
      )}
      {cta && (
        <a
          href={href}
          style={{
            color: "#fff",
            background: "#2c5f8d",
            padding: "10px 18px",
            borderRadius: 9999,
            fontSize: 13,
            fontWeight: 700,
            textDecoration: "none",
            marginTop: 6,
          }}
        >
          {cta}
        </a>
      )}
    </div>
  );
}

function Router() {
  const { session, profile, loading, recoveryMode, signOut } = useAuth();
  const [hash, setHash] = useState(window.location.hash || "#");
  const [pathname, setPathname] = useState(window.location.pathname);

  useEffect(() => {
    const onHash = () => setHash(window.location.hash || "#");
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener("hashchange", onHash);
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener("popstate", onPop);
    };
  }, []);

  // Toss Payments return paths — handled before any other route
  if (pathname === "/pay/success") return <PayReturn mode="success" />;
  if (pathname === "/pay/fail") return <PayReturn mode="fail" />;

  // Auto-redirect operators landing at root → admin queue (skip during recovery)
  useEffect(() => {
    if (!loading && !recoveryMode && profile?.role === "operator" && hash === "#") {
      window.location.hash = "#admin";
    }
  }, [loading, recoveryMode, profile, hash]);

  // Post-logout redirect from admin → root
  useEffect(() => {
    if (!loading && !session && hash.startsWith("#admin")) {
      window.location.hash = "";
    }
  }, [loading, session, hash]);

  // Password recovery takes precedence over any other route
  if (recoveryMode) {
    return (
      <div className="app-shell">
        <ScreenPasswordReset />
      </div>
    );
  }

  const workMatch = hash.match(/^#admin\/work\/([0-9a-f-]{8,})$/i);
  const workCaseId = workMatch ? workMatch[1] : null;
  const isRefundsRoute = hash === "#admin/refunds";
  const isFreeGrantsRoute = hash === "#admin/free-grants";
  const isArchiveRoute = hash === "#admin/archive";
  const archiveDetailMatch = hash.match(/^#admin\/archive\/([0-9a-f-]{8,})$/i);
  const archiveCaseId = archiveDetailMatch ? archiveDetailMatch[1] : null;
  const isParentsRoute = hash === "#admin/parents";
  const parentDetailMatch = hash.match(/^#admin\/parents\/([0-9a-f-]{8,})$/i);
  const parentDetailId = parentDetailMatch ? parentDetailMatch[1] : null;
  const isStatsRoute = hash === "#admin/stats";
  const isAdminRoute =
    hash === "#admin" || workCaseId !== null
    || isRefundsRoute || isFreeGrantsRoute
    || isArchiveRoute || archiveCaseId !== null
    || isParentsRoute || parentDetailId !== null
    || isStatsRoute;

  if (isAdminRoute) {
    if (loading) return <ShellLoading />;
    if (!session) {
      return (
        <AccessGate
          title="로그인이 필요해요"
          body="운영진 화면은 로그인한 뒤 접근할 수 있어요."
          cta="홈으로"
          href="#"
        />
      );
    }
    if (profile?.role !== "operator") {
      return (
        <AccessGate
          title="운영진만 접근할 수 있어요"
          body={profile?.email ? `${profile.email} 계정에는 운영진 권한이 없어요.` : null}
          cta="홈으로"
          href="#"
        />
      );
    }

    // Operator view — logout lives in each admin screen's sidebar profile area.
    const handleSignOut = () => {
      if (window.confirm("로그아웃 하시겠어요?")) signOut();
    };

    if (workCaseId) {
      return (
        <div className="fullscreen-shell">
          <AdminWorkspace
            caseId={workCaseId}
            operatorId={session.user.id}
            onBack={() => { window.location.hash = "#admin"; }}
            onSubmit={() => { window.location.hash = "#admin"; }}
            onSignOut={handleSignOut}
          />
        </div>
      );
    }
    if (isRefundsRoute) {
      return (
        <div className="fullscreen-shell">
          <AdminRefunds onSignOut={handleSignOut} />
        </div>
      );
    }
    if (isFreeGrantsRoute) {
      return (
        <div className="fullscreen-shell">
          <AdminFreeGrants onSignOut={handleSignOut} />
        </div>
      );
    }
    if (archiveCaseId) {
      return (
        <div className="fullscreen-shell">
          <AdminArchiveDetail
            caseId={archiveCaseId}
            onBack={() => { window.location.hash = "#admin/archive"; }}
            onSignOut={handleSignOut}
          />
        </div>
      );
    }
    if (isArchiveRoute) {
      return (
        <div className="fullscreen-shell">
          <AdminArchive onSignOut={handleSignOut} />
        </div>
      );
    }
    if (parentDetailId) {
      return (
        <div className="fullscreen-shell">
          <AdminParentDetail
            userId={parentDetailId}
            onBack={() => { window.location.hash = "#admin/parents"; }}
            onSignOut={handleSignOut}
          />
        </div>
      );
    }
    if (isParentsRoute) {
      return (
        <div className="fullscreen-shell">
          <AdminParents onSignOut={handleSignOut} />
        </div>
      );
    }
    if (isStatsRoute) {
      return (
        <div className="fullscreen-shell">
          <AdminStats onSignOut={handleSignOut} />
        </div>
      );
    }
    return (
      <div className="fullscreen-shell">
        <AdminDashboard
          onOpen={(caseId) => { window.location.hash = `#admin/work/${caseId}`; }}
          onSignOut={handleSignOut}
        />
      </div>
    );
  }

  // Default — parent app handles its own auth branching
  return <ParentApp />;
}

export default function App() {
  return (
    <AuthProvider>
      <Router />
    </AuthProvider>
  );
}
